/* eslint-disable tailwindcss/enforces-negative-arbitrary-values */
import {
  RC_DURATION,
  REEBELO_STORE_TO_LANGUAGE,
  ReebeloConditions,
} from '@lambda/reebelo';
import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { invert, isEmpty } from 'lodash-es';
import Head from 'next/head';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import { convertCellularFieldsToServiceProvider } from '@lambda/business/offers/cellular';
import dynamic from 'next/dynamic';
import { AppCtx } from 'pages/_app';
import { ServiceProvider } from '@lambda/commons/apis/elasticsearch/types';
import dayjs from 'dayjs';
import { strToHandle } from '@lambda/reebelo/src/commonHelpers';
import { handleFreeReturn } from '@lambda/commons/helpers';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import settings from '@/settings';
import { replacePlusSymbol } from '../../../../pages/products/[handle]';
import {
  OfferTree,
  SelectionT,
  selectWarranty,
  ReplacedProduct,
  generateAltTagForOffer,
} from '@/lib/collections/catalogue/helpers';
import { NewProductProps, ShippingData } from './types';
import useVendorDetails, { useVendorDispatch } from '@/lib/use-vendor-details';
import MicroData from '@/components/collections/[handle]/Microdata';
import Metadata from '@/components/collections/[handle]/Metadata';
import ProductImageSlider from '@/components/products/ProductImageSlider';
import BackToTopButton from '@/components/collections/BackToTopButton';
import GTM from '@/lib/google-tag-manager';
import i18n from '@/settings/i18n';
import TrustpilotWidget from '@/components/products/TrustpilotWidget';
import usePageScrollEvent from './usePageScrollEvent';
import { getShortenCountryName } from '@/lib/use-origin-country';
import useDeliveryDate from '@/lib/use-delivery-date';
import useExpressShippingOffer from '@/lib/use-express-shipping-offer';
import DefaultSeo from '../DefaultSeo';
import useComparePrices from '@/lib/use-pim-compare-price';
import PromoMessages from './PromoMessages';
import { OfferDealMap } from '@/lib/hot-deals/helpers';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import RecommendedAccessories from '@/components/products/RecommendedAccessories';
import { useRecentlyViewed } from '@/lib/use-recently-viewed';
import TopBar from '@/components/products/TopBar';
import fetchDealViewsCount from '@/lib/use-product-deal-view-count';
import { GoogleTopQualityBadge } from '@/lib/use-google-badge';
import SustainableBadge from './SustainableBadge';
import ProductSoldInfo from './ProductSoldInfo';
import ProductInfoSection from './ProductInfoSection';
import VariantSelectionSection from './VariantSelectionSection';
import ShippingOptionsSection from './ShippingOptionsSection';
import AboutProductSection from './AboutProductSection';
import {
  checkIsTradeInEligible,
  mostPopularProducts,
  shouldShowPromoMessage,
} from '@/lib/collections/helpers';
import { VendorKey } from '@/settings/vendors/types';
import vendors from '@/settings/vendors';
import BrandBadge from './BrandBadge';
import TradeInModal from './TradeIn/TradeInModal';
import CompactTradeIn from './TradeIn/CompactTradeIn';

import {
  trackReebeloCareDeselected,
  trackReebeloCareSelected,
  trackViewed,
} from '@/lib/analytics/trackProduct';
import { ReebeloCareTrackRequest } from '@/lib/analytics/types';
import PopularCategories from '@/components/commons/PopularCategories';
import Breadcrumb from '@/components/collections-v2/common/Breadcrumb';
import { generateCategoryPageUrl } from '@/components/category/helpers';
import BreadcrumbJsonLd from '@/components/seo/BreadcrumbJsonLd';
import { useCustomerContext } from '@/lib/customer/CustomerContext';

const t = i18n.init();

const Marketplace = dynamic(() => import('../../../commons/Marketplace'), {
  ssr: false,
});

const CircularEconomy = dynamic(
  () => import('../../../homepage/CircularEconomy'),
  {
    ssr: false,
  },
);
const HotDealSlider = dynamic(
  () => import('@/components/homepage/HotDealSlider'),
  { ssr: false },
);
const RecentlyViewed = dynamic(
  () => import('@/components/homepage/RecentlyViewed'),
  { ssr: false },
);
const YouMayAlsoLike = dynamic(
  () => import('@/components/collections-v2/common/YouMayAlsoLike'),
  { ssr: false },
);
const MoreFromBrand = dynamic(() => import('../../../products/MoreFromBrand'), {
  ssr: false,
});
const Usps = dynamic(() => import('../Usps'), {
  ssr: false,
});
const MoreProductInfo = dynamic(() => import('../MoreProductInfo'), {
  ssr: false,
});
const ProductReviews = dynamic(
  () => import('@/components/collections/reviews/ProductReviews'),
  { ssr: false },
);

const freeReturnDays = handleFreeReturn(
  settings.store,
  settings.free_return_days,
);

const Section = (props: {
  children?: ReactNode;
  className?: string;
  id?: string;
}) => (
  <div
    className={props.className ? props.className : 'relative z-0 py-8'}
    id={props.id ?? ''}
  >
    {props.children}
  </div>
);

export default function NewProduct(props: NewProductProps) {
  const router = useRouter();

  const {
    product,
    offers,
    isUnfitProduct,
    dynamicVariantGroups,
    dynamicVariantGroupsNames,
    reviews,
    categoryWarrantyInfo,
    categoryPk,
    categoryParentName,
    breadcrumb,
    categorySummary,
  } = props;

  const { customer } = useCustomerContext();
  const [warrantyID, setWarrantyID] = useState<number | null>(null);
  const [reviewsInState, setReviewsInState] = useState(reviews);
  const [showTradeInModal, setShowTradeInModal] = useState(false);

  const offerTree = useMemo(
    () =>
      new OfferTree(offers, product.category, dynamicVariantGroupsNames, {
        handle: props.handle,
      }),
    [JSON.stringify(offers.map((o) => o.productId))],
  );

  const { store } = settings;

  const isUSStore = store === 'reebelo-us';

  const isSmartphone = product.category === 'Smartphones';

  const offer = useMemo<ReplacedProduct>(
    () =>
      offerTree.get({
        ...router.query,
        serviceProvider:
          isUSStore && isSmartphone
            ? router.query.serviceProvider || 'Unlocked'
            : router.query.serviceProvider,
      } as Record<string, string>),
    [router.query, offerTree],
  );

  const extractedTags = extractTags(offer.tags);

  // fetch delivery by via API
  const { zipcodeStateData, setZipcodeStateData } = useContext(AppCtx);
  const deliveryDate = useDeliveryDate({
    zipcode: zipcodeStateData?.zipcode,
    state: zipcodeStateData?.state,
    category: product.category,
    vendor: offer.vendor,
  });

  const [shippingData, setShippingData] = useState<ShippingData>({
    type: 'standard',
    value: 0,
    cutoffTime: deliveryDate?.standard.cutoffTime || '',
    cutoffTimezone: deliveryDate?.standard.cutoffTimezone || '',
  });

  const selection: SelectionT = {
    ...extractedTags,
    condition: extractedTags.condition?.replace('Very Good', 'Good'),
    serviceProvider:
      settings.store === 'reebelo-us'
        ? (router.query.serviceProvider as string) ||
          convertCellularFieldsToServiceProvider(extractedTags)?.[0]
        : undefined,
  };

  const dispatch = useVendorDispatch(offer.vendor);

  // variables
  const language = REEBELO_STORE_TO_LANGUAGE[settings.store];
  const colors =
    props.product.locale?.[language]?.colors ?? props.product.colors;
  const productName = product.name;

  const colorsToCodeMapping = invert(colors);
  const colorCode = colorsToCodeMapping[selection.color as any];

  const images: string[] =
    (props.product.images[colorCode as any] as []) ??
    Object.values(product.images)?.[0];

  const showCarrier = settings.store === 'reebelo-us' && offer.serviceProvider;

  // Description show more.
  const [isConditionOpen, setIsConditionOpen] = useState(false);
  const [productWarranty, setProductWarranty] = useState<
    WarrantyT | undefined
  >();

  const titlePrefix = 'Up to 70% off Certified Refurbished';
  const title =
    titlePrefix +
    t` ${{
      name: replacePlusSymbol(productName),
    }}`;
  const description = t`Get the best price for unlocked ${{
    name: replacePlusSymbol(productName),
  }} in ${{
    country: getShortenCountryName(),
  }}. Shop a wide collection at up to 70% off retail. Free shipping, 1 year warranty, and ${{
    free_return_days: freeReturnDays,
  }} day returns included.`;

  const comparePrice = useComparePrices({
    psku: offer.psku,
    existingPimComparePrice: product.comparePrice,
    storage: offer.storage,
    tags: offer.tags,
    actualPrice: offer.price,
    fallbackPrice: offer.comparePrice,
  });

  const category =
    props?.product?.category ??
    offer.categories?.[0] ??
    offer.productType ??
    extractedTags.category;
  const brand = props?.product?.brand ?? offer.brand ?? extractedTags.brand;

  const eligibleWarranty = productWarranty && productWarranty.shopifyVariantId;

  const handleWarrantyClick = () => {
    if (!eligibleWarranty) return;

    const trackingProps: ReebeloCareTrackRequest = {
      entry_point: 'Product Page',
      reebelocare_id: productWarranty.shopifyVariantId,
      device_psku: product.psku,
      device_vendor: offer.vendor,
      device_sku: offer?.sku ?? '',
      duration: RC_DURATION['24_MONTH'],
    };

    if (warrantyID !== null) {
      // Deselect ReebeloCare
      trackReebeloCareDeselected(trackingProps);
      setWarrantyID(null);
    } else {
      // Select ReebeloCare
      trackReebeloCareSelected(trackingProps);
      setWarrantyID(productWarranty.shopifyVariantId);
    }
  };

  const handleShippingTypeChanged = (data: ShippingData) => {
    setShippingData(data);
  };

  useEffect(() => {
    GTM.eventOffers('view_item', [offer]);

    const { key, productId, categories, productType, price, variantId } = offer;
    const eventPayload = {
      detail: {
        actionField: { list: `${productType} list` },
        products: [
          {
            price,
            brand: offer.brand,
            name: title,
            id: key || productId,
            category:
              categories && categories.length
                ? categories[0]
                : productType || '',
            variant: variantId,
          },
        ],
      },
    };

    GTM.event(null, eventPayload);
  }, [offer]);

  useEffect(() => {
    const handleWarrantySelection = async () => {
      if (!props.isWarrantyEligible) return;
      const { price } = offer;
      const warranty = await selectWarranty(
        price,
        RC_DURATION['24_MONTH'],
        category,
      );

      setProductWarranty(warranty);
    };

    handleWarrantySelection();
  }, [offer.key]);

  useEffect(() => {
    // previously selected and eligible for warranty
    if (warrantyID !== null && eligibleWarranty)
      setWarrantyID(productWarranty.shopifyVariantId);

    // reset warrany if illegible warranty
    if (!eligibleWarranty) setWarrantyID(null);
  }, [offer.key, productWarranty]);

  // reset warranty selection when changing page
  useEffect(() => {
    setWarrantyID(null);
  }, [props.handle]);

  const { shouldShowTopBar } = usePageScrollEvent();

  useEffect(() => {
    setShippingData({
      type: 'standard',
      value: 0,
      cutoffTime: deliveryDate?.standard.cutoffTime || '',
      cutoffTimezone: deliveryDate?.standard.cutoffTimezone || '',
    });
  }, [offer.key, deliveryDate?.standard.cutoffTime]);

  const expressShippingOffer = useExpressShippingOffer({
    vendor: offer.vendor,
    categoryPk,
    isEnabled:
      deliveryDate != null &&
      deliveryDate.express.minDeliveryDate !== '' &&
      categoryPk !== 'undefined',
  });

  const productTitle = useMemo(() => {
    let _productTitle = [
      productName,
      selection.storage,
      selection.watchCaseMaterial,
      selection.watchFaceSize,
      selection.watchCaseColor,
      selection.watchBandMaterial,
      selection.watchBandType,
      selection.watchBandColor,
      selection.charger,
      selection.touchbar,
      // Only add color if it is not a smart watch
      !selection.watchCaseColor ? selection.color : null,
      selection.connectivity,
      selection.cpu,
      selection.ram,
      selection.simSlot,
    ]
      .filter(Boolean)
      .join(' - ');

    if (isUSStore && showCarrier)
      _productTitle += ` - ${selection.serviceProvider}`;

    return _productTitle;
  }, [productName, selection, isUSStore, showCarrier]);

  const deal = OfferDealMap([...offers], router.query, offer);

  const hasOnlyHandleProperty =
    typeof router.query === 'object' &&
    Object.keys(router.query).length === 1 &&
    'handle' in router.query;

  const { reviews: productReviews, summary: productReviewsSummary } =
    reviews || {
      summary: {
        count: 0,
        stars: 0,
        minRating: 3.5,
        maxRating: 5,
        countAbove3andAHalf: 0,
        starPercentages: {
          '1': 0,
          '2': 0,
          '3': 0,
          '4': 0,
          '5': 0,
        },
      },
      reviews: [],
    };

  const defaultAltTag = selection
    ? generateAltTagForOffer(
        {
          ...offer,
          condition: selection?.condition as ReebeloConditions,
          serviceProviders: selection?.serviceProvider
            ? [selection?.serviceProvider as ServiceProvider]
            : [],
        },
        productName,
      )
    : offer.defaultImageAltTag;

  const purchaseLinkOffer = offer;

  const [recentlyViewed, fetchingRecentlyViewed] = useRecentlyViewed({
    name: offer.title,
    image:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      product.images[colorCode as any]?.[0] ??
      Object.values(product.images)?.[0]?.[0] ??
      product.collectionImageUrl ??
      '',
    type: 'collection',
    handle: router.query.handle as string,
    sku: offer?.sku ?? '',
    tags: offer.tags,
    addedAt: dayjs().format(),
    price: offer.price,
    comparePrice,
    summary: productReviewsSummary,
    isCheapest: offers.filter((o) => o.price < offer.price).length === 0,
    vendor: offer.vendor,
  });

  const warrantyPrice = warrantyID != null ? productWarranty?.price : 0;
  const totalPrice = offer.price + shippingData.value + (warrantyPrice ?? 0);

  useEffect(() => {
    if (offer.isDeal) {
      const dealEndInSEC = offer?.dealEndDate
        ? dayjs(`${offer.dealEndDate}T23:59:59`).diff(dayjs(), 'second')
        : 0;

      fetchDealViewsCount(!!offer.isDeal, offer?.sku || '', dealEndInSEC);
    }
  }, [router.query]);

  useEffect(() => {
    trackViewed({
      offer,
      customer,
      properties: {
        compare_price: comparePrice,
        condition: (selection.condition ||
          offer.condition) as ReebeloConditions,
      },
    });
  }, [router.query, customer]);

  const vendorInfo = useVendorDetails(purchaseLinkOffer.vendor);
  const vendorHandle = strToHandle(vendorInfo?.displayName);
  const certifiedVendor = vendors[vendorHandle as VendorKey];
  const isCertifiedVendor = !isEmpty(certifiedVendor);

  const isTradeInEligible = checkIsTradeInEligible();

  const breadcrumbSeo = [
    { position: 0, name: 'Home', item: '/' },
    ...breadcrumb.map((c, index) => {
      const link = generateCategoryPageUrl(c.name, c.pk);

      return {
        position: index + 1,
        name: c.name,
        item: link,
      };
    }),
    {
      position: breadcrumb.length + 1,
      name: productTitle,
      item: `/collections/${router.query.handle}`,
    },
  ];

  const origin = dispatch.note.startsWith('Ships from')
    ? dispatch.note
        .replace('Ships from ', '')
        .replace('United States of America', 'United States')
    : vendorInfo?.company.country;

  return (
    <div id="old-multi-variant-pdp">
      <GoogleTopQualityBadge />
      <Head>
        {/* title tag need to be direct children in order to prevent overwritten */}
        <title>{title}</title>
        <WebsiteSchema />
        <Metadata
          offer={offer}
          product={product}
          title={title}
          description={description}
          handle={props.handle}
        />
        <MicroData
          selected={offer}
          offers={offers}
          product={product}
          outOfStock={isUnfitProduct}
          handle={
            hasOnlyHandleProperty
              ? (router.query.handle as string)
              : offer.handle
          }
          defaultAltTag={defaultAltTag}
          vendor={offer.vendorDetails?.displayName}
          productReviewsSummary={productReviewsSummary}
          isOrganicAdRedirect={props.isOrganicAdRedirect}
        />
        <BreadcrumbJsonLd itemListElement={breadcrumbSeo} host={props.host} />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
      </Head>
      <BackToTopButton />
      {/* Stable Top Bar - Avoid updates unless absolutely necessary */}
      {shouldShowTopBar && (
        <TopBar
          {...{
            isTradeInEligible,
            selection,
            purchaseLinkOffer,
            comparePrice,
            warrantyID,
            shippingData,
            deliveryDate,
            isUnfitProduct,
            totalPrice,
          }}
          productTitle={productTitle}
          categoryPk={categoryPk}
        />
      )}
      {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
      <div className=" bg-white pt-5 lg:pt-6" id="ga-prod-page">
        <div className="lg:reebelo-container">
          <Breadcrumb
            breadcrumb={breadcrumb}
            productTitle={productName}
            className="mb-2 hidden lg:flex"
          />
          <div className="flex flex-col items-start lg:flex-row lg:gap-4">
            {/* Left Panel */}
            <section
              className={cn(
                'w-full select-none rounded-md transition-all duration-200 ease-in-out lg:sticky lg:w-1/2',
                {
                  'lg:top-[9rem]': !shouldShowTopBar,
                  'lg:top-[7.2rem]': shouldShowTopBar,
                },
              )}
            >
              <div className="rounded-md border-gray-200 px-5 sm:px-8 lg:border lg:p-4">
                <div className="flex flex-wrap items-center justify-between lg:mb-10 ">
                  <div className="flex w-full items-center justify-between truncate lg:hidden">
                    <Breadcrumb
                      breadcrumb={breadcrumb}
                      productTitle={productName}
                      isMobile
                    />
                    <div className="text-sm lg:hidden">
                      <ProductSoldInfo
                        psku={product.psku}
                        productName={productName}
                        mostPopularProducts={mostPopularProducts}
                      />
                    </div>
                  </div>

                  <div className="mr-3 hidden truncate lg:block">
                    {isCertifiedVendor ? <BrandBadge /> : <SustainableBadge />}
                  </div>
                  <div className="hidden text-sm lg:block">
                    <ProductSoldInfo
                      psku={product.psku}
                      productName={productName}
                      mostPopularProducts={mostPopularProducts}
                    />
                  </div>
                </div>
                <div className="hidden lg:block">
                  <ProductImageSlider
                    images={images}
                    defaultAltTag={defaultAltTag}
                  />
                  <TrustpilotWidget
                    desktopAlign="center"
                    mobileAlign="center"
                    className="mb-2 mt-2 text-center text-xs xs:mt-4 xs:text-sm sm:mb-0 sm:mt-0 sm:text-base lg:mt-7"
                    starClassName="h-3 w-3 xs:h-4 xs:w-4"
                  />
                </div>
              </div>
            </section>
            {/* Right Panel */}
            <section className="relative w-full  px-5 py-2 sm:px-8 lg:w-1/2 lg:p-0">
              <div className="relative z-20 mx-auto mt-1 w-full  rounded bg-white px-0 sm:mt-2 lg:mt-0 lg:rounded-none lg:p-0">
                {/* Product Section  */}
                <ProductInfoSection
                  productTitle={productTitle}
                  productReviewsSummary={productReviewsSummary}
                  totalPrice={totalPrice}
                  comparePrice={comparePrice}
                  offer={offer}
                  shouldShowPromoMessage={shouldShowPromoMessage(
                    store,
                    offer.price,
                  )}
                  category={category}
                  purchaseLinkOffer={purchaseLinkOffer}
                  product={product}
                  warrantyID={warrantyID}
                  shippingData={shippingData}
                  deliveryDate={deliveryDate}
                  selection={selection}
                  isUnfitProduct={isUnfitProduct}
                  isCertifiedVendor={isCertifiedVendor}
                  showTradeInModal={showTradeInModal}
                  setShowTradeInModal={setShowTradeInModal}
                  isTradeInEligible={isTradeInEligible}
                  categoryWarrantyInfo={categoryWarrantyInfo}
                  categoryPk={categoryPk}
                  origin={origin}
                  breadcrumb={breadcrumb}
                />
                {/* Mobile Image Slider Section */}
                <div className="relative my-3 select-none text-center lg:hidden">
                  <ProductImageSlider
                    images={images}
                    defaultAltTag={defaultAltTag}
                  />
                </div>
                {/* Mobile USPs */}
                <div className="lg:hidden">
                  <Usps
                    deliveryDate={deliveryDate}
                    productCondition={selection.condition ?? ''}
                    categoryWarrantyInfo={categoryWarrantyInfo}
                    origin={origin}
                    breadcrumb={breadcrumb}
                  />
                </div>
                {/* Mobile Trade-In */}
                <div
                  className={cn('pt-8 pb-4 text-center lg:hidden', {
                    hidden: !isTradeInEligible,
                  })}
                >
                  <CompactTradeIn
                    setShowTradeInModal={setShowTradeInModal}
                    eligible={isTradeInEligible}
                  />
                </div>
                {/* Mobile PromoMessages */}
                <div
                  className={cn(
                    '-mx-5 border-t border-gray-200 pt-1 text-center sm:-mx-8 lg:hidden',
                    {
                      hidden: !shouldShowPromoMessage(store, offer.price),
                      'mt-5': !isTradeInEligible,
                    },
                  )}
                >
                  <PromoMessages
                    price={offer.price}
                    type="new-pdp"
                    category={category}
                    sku={offer?.sku}
                  />
                </div>
                {/* Mobile Image Slider + Options Separator */}
                <hr
                  className={cn(
                    '-mx-5 mb-4 h-[1px] bg-gray-200 sm:-mx-8 lg:hidden',
                    {
                      'mt-4': shouldShowPromoMessage(store, offer.price),
                    },
                  )}
                />
                {/* Variant Selection Section + Warranty */}
                <VariantSelectionSection
                  selection={selection}
                  isConditionOpen={isConditionOpen}
                  setIsConditionOpen={setIsConditionOpen}
                  product={product}
                  offerTree={offerTree}
                  language={language}
                  colors={colors}
                  showCarrier={showCarrier}
                  isUnfitProduct={isUnfitProduct}
                  dynamicVariantGroups={dynamicVariantGroups}
                  deal={deal}
                  offer={offer}
                  productWarranty={productWarranty}
                  handleWarrantyClick={handleWarrantyClick}
                  warrantyID={warrantyID}
                  offers={offers}
                  setShowTradeInModal={setShowTradeInModal}
                  isTradeInEligible={isTradeInEligible}
                  categoryPk={categoryPk}
                />
                {/* Shipping Information Section  */}
                <ShippingOptionsSection
                  offer={offer}
                  deliveryDate={deliveryDate}
                  expressShippingOffer={expressShippingOffer}
                  dispatch={dispatch}
                  purchaseLinkOffer={purchaseLinkOffer}
                  productName={productName}
                  handleShippingTypeChanged={handleShippingTypeChanged}
                  selection={selection}
                  categoryPk={categoryPk}
                />
                {/* Product Info Section - About this item  */}
                <AboutProductSection
                  product={product}
                  offer={offer}
                  isUnfitProduct={isUnfitProduct}
                  dispatch={dispatch}
                  zipcodeStateData={zipcodeStateData}
                  setZipcodeStateData={setZipcodeStateData}
                  vendor={purchaseLinkOffer.vendor}
                  vendorInfo={vendorInfo}
                  productDetails={{
                    source: 'Multi Variant Product Page',
                    brand: offer?.brand,
                    category: offer?.categories?.join('|'),
                    color: offer?.color,
                    condition: offer?.condition,
                    storage: offer?.storage,
                    name: offer?.title?.split(' - ')?.[0],
                    title: offer?.title,
                    price: offer?.price?.toString(),
                    psku: offer?.psku,
                    vendor: offer?.vendor,
                    vendorSku: offer?.sku,
                  }}
                  categorySummary={categorySummary}
                />
              </div>
            </section>
          </div>
        </div>
      </div>
      <div className="bg-white">
        <div className="reebelo-container">
          <MoreProductInfo
            title="More about this item"
            psku={product.psku}
            description={
              product.locale?.[language]?.description ?? product.description
            }
            category={product.category}
          />
          {/* Recommended Accessories  */}
          <RecommendedAccessories tags={offer.tags} />
          {/* You May Also Like */}
          <Section>
            <YouMayAlsoLike
              categoryParentName={categoryParentName}
              category={category}
              brand={brand}
              key={offer.productId}
            />
          </Section>
          {/* More From Brand  */}
          <MoreFromBrand
            brand={
              extractTags(offer.tags).brand ??
              offer.brand ??
              product.brand ??
              ''
            }
            category={
              extractTags(offer.tags).category ??
              offer.categories?.[0] ??
              product.category ??
              ''
            }
          />
          {/* Hot Deals Slider  */}
          <HotDealSlider />
          {/*  Popular Categories */}
          <PopularCategories />
          {/* Recently Viewed  */}
          <RecentlyViewed
            recentlyViewed={recentlyViewed}
            fetching={fetchingRecentlyViewed}
          />
          {/* Product Reviews  */}
          <Section id="reviews-slider">
            <ProductReviews
              product={{
                collectionImageUrl: product.collectionImageUrl ?? '',
                name: productName,
                images: product.images,
                colors,
                psku: product.psku,
              }}
              type="multi-variant-reviews"
              reviews={reviewsInState?.reviews ?? productReviews}
              summary={productReviewsSummary}
              selectedImage={images[0]}
              setReviewsInState={setReviewsInState}
              reviewsInState={reviewsInState}
            />
          </Section>
          <MoreProductInfo
            psku={product.psku}
            description={product.faq}
            title="Frequently Asked Questions"
          />
        </div>
        <Section className="bg-teal-500 pt-8">
          <Marketplace view="product" category={category} />
        </Section>
        <div className="py-5 lg:hidden">
          <TrustpilotWidget
            desktopAlign="center"
            mobileAlign="center"
            className="text-center text-xs xs:text-sm sm:text-base"
            starClassName="h-3 w-3 xs:h-4 xs:w-4"
          />
        </div>
      </div>
      <div className="bg-gray-300">
        <div className="reebelo-container">
          <Section>
            <CircularEconomy />
          </Section>
          <DefaultSeo
            isUSStore={isUSStore}
            productName={productName}
            description={
              product.locale?.[language]?.description ?? product.description
            }
            isSmartphone={isSmartphone}
            productBrand={product.brand}
          />
        </div>
      </div>
      <TradeInModal
        showModal={showTradeInModal}
        hideModal={() => setShowTradeInModal(false)}
      />
    </div>
  );
}
